// Type imports
import type { MenuLink, ResolvedMenuNode } from "~/types/storyblok/storyblok";
import type { Locale } from "~/types/locale";

// Regular imports
import { useFetchCategoryTree } from "~/composables/useFetchCategoryTree";
import { useLoadUser } from "~/composables/useLoadUser";
import { useStoryblokFetcher } from "~/composables/useStoryblokFetcher";
import { extractFooterFields } from "~/utils/extractFooterFields";
import { getCulturesFromLocales } from "~/utils/getCulturesFromLocales";

export const useInitApp = () => {
	const appStore = useAppStore();
	// `useCartInit` captures Nuxt composables (useRoute, useLocalePath,
	// navigateTo) here in setup so they survive the Pinia action microtask.
	const { initCart } = useCartInit();
	const productStore = useProductStore();
	const { fetchStory } = useStoryblokFetcher();
	const {
		getApplication,
		getParametricsInformation,
		getPriceLists,
		getMenuCategoriesFromNorce
	} = useNorcePwa();
	const { fetchCategoryTree } = useFetchCategoryTree();
	const { getUserByEmailIfHasSession } = useLoadUser();
	const norceStore = useNorceStore();
	const config = useRuntimeConfig();
	const nuxtApp = useNuxtApp();
	const { loadPromotionTexts } = usePromotionTexts();

	const initApp = async (
		iso: string,
		locale: string,
		locales: Locale[] = [],
		storyblokLanguage?: string
	) => {
		norceStore.setCultureCode(iso);
		norceStore.setLocaleCode(locale);
		const [{ data }, categoriesTree, priceLists, , , menu] = await Promise.all([
			fetchStory("layouts/main", {
				version: config.public.storyblokVersion,
				language: storyblokLanguage || iso,
				resolve_links: "url"
			}),
			fetchCategoryTree(locale),
			getPriceLists({ cultureCode: iso }),
			nuxtApp.runWithContext(() => getUserByEmailIfHasSession()),
			loadPromotionTexts(locale, iso),
			// The Menu Node menu (GF-85). An empty list, also on failure, makes the
			// header fall back to the old menu_categories below.
			$fetch<ResolvedMenuNode[]>("/api/menu", {
				query: { locale: storyblokLanguage || iso, cultureCode: iso }
			}).catch((err) => {
				console.warn(
					"[useInitApp] /api/menu failed, falling back to the old menu:",
					err instanceof Error ? err.message : err
				);
				return [] as ResolvedMenuNode[];
			})
		]);
		const application = await getApplication(locale);

		setColors(data.story.content.colors);

		const appData = {
			// Needed by the base layout, which re-applies the theme on the client
			// after hydration (setColors is a no-op server-side).
			colors: data.story.content.colors,
			siteLogo: data.story.content.site_logo,
			siteLogoDimensions: {
				width: Number(
					data.story.content.site_logo.filename.split("/")[5].split("x")[0]
				),
				height: Number(
					data.story.content.site_logo.filename.split("/")[5].split("x")[1]
				)
			},
			search: data.story.content.search,
			account: data.story.content.account,
			topNavbarLeft: data.story.content.top_navbar_left,
			topBarItems: data.story.content.top_bar?.[0]?.items,
			menu,
			menuCategories: data.story.content.menu_categories.filter(
				(menuCat: MenuLink) => menuCat.component !== "categories_multi_list"
			),
			menuCategoriesNorce: getMenuCategoriesFromNorce({
				categoriesTree,
				norceMenuCategoriesFromStoryblok:
					data.story.content.menu_categories.filter(
						(menuCat: MenuLink) => menuCat.component === "categories_multi_list"
					)[0]?.categories
			}),
			applicationNorce: application,
			// Language list comes from the env-driven LOCALES (via i18n), not from
			// the Norce application cultures: only locales configured here have a
			// route to navigate to. Order follows LOCALES.
			culturesNorce: getCulturesFromLocales(locales),
			currenciesNorce: norceStore.currenciesNorce,
			headerLoaded: true,
			flags: data.story.content.flags,
			customerServicePhone: data.story.content.customer_service_phone,
			stockStatus: data.story.content.stock_status,
			footer: extractFooterFields(data.story.content),
			cartRecommendations: data.story.content.cart_recommendations?.[0] || null
		};

		appStore.initState(appData);
		norceStore.initState(application);

		const paramInfo = await getParametricsInformation({
			...norceStore.storeContext
		});
		productStore.addParametricsInformation(paramInfo);
		productStore.addDefaultProductImage(
			data.story.content.default_image.filename
		);
		productStore.setPriceLists(priceLists);
		// Not the store directly: useCartInit keeps a completed order's basket for
		// the success page's purchase event [GF-94].
		await initCart();
	};

	return {
		initApp
	};
};
